import { Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { useAuthStore } from './store/auth.store'
import DashboardLayout from './layouts/DashboardLayout'
import { Toaster } from 'sonner'
import { useTheme } from 'next-themes'

const Login = lazy(() => import('./pages/Login'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Employees = lazy(() => import('./pages/Employees'))
const EmployeeDetails = lazy(() => import('./pages/EmployeeDetails'))
const Projects = lazy(() => import('./pages/Projects'))
const ProjectDetails = lazy(() => import('./pages/ProjectDetails'))
const Reports = lazy(() => import('./pages/Reports'))
const Screenshots = lazy(() => import('./pages/Screenshots'))
const Activity = lazy(() => import('./pages/Activity'))
const Settings = lazy(() => import('./pages/Settings'))
const Live = lazy(() => import('./pages/Live'))
const Teams = lazy(() => import('./pages/Teams'))
const Notifications = lazy(() => import('./pages/Notifications'))
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'))

function PageFallback() {
  return (
    <div className="flex justify-center items-center h-[50vh] text-default-500" role="status" aria-label="Loading page">
      <Loader2 className="w-8 h-8 text-spotify-green animate-spin" />
    </div>
  )
}

function ThemedToaster() {
  const { theme } = useTheme()
  // Merge: remote theme-aware Toaster + local richColors/closeButton affordances.
  return <Toaster theme={theme === 'dark' ? 'dark' : 'light'} richColors closeButton position="top-right" />
}

function NotFound() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-foreground mb-4">404</h1>
        <p className="text-xl text-muted mb-6">Page not found</p>
        <Link to="/" className="bg-spotify-green text-black font-bold rounded-full px-6 py-2 hover:bg-spotify-green-hover transition-colors inline-block">
          Go Home
        </Link>
      </div>
    </div>
  )
}

function PrivateRoute({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const hasHydrated = useAuthStore((state) => state._hasHydrated)
  const loc = useLocation()
  if (!hasHydrated) return null
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace state={{ from: loc.pathname }} />
}

function RoleRoute({ children, allow }: { children: ReactNode; allow: Array<'admin' | 'manager'> }) {
  const user = useAuthStore((state) => state.user)
  if (!user || !allow.includes(user.role as 'admin' | 'manager')) {
    return <Navigate to="/" replace />
  }
  return <>{children}</>
}

export default function App() {
  return (
    <>
      <ThemedToaster />
      <Router>
        <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />

          <Route
            path="/"
            element={
              <PrivateRoute>
                <DashboardLayout />
              </PrivateRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="employees" element={<RoleRoute allow={['admin', 'manager']}><Employees /></RoleRoute>} />
            <Route path="employees/:id" element={<EmployeeDetails />} />
            <Route path="projects" element={<Projects />} />
            <Route path="projects/:id" element={<ProjectDetails />} />
            <Route path="reports" element={<Reports />} />
            <Route path="live" element={<RoleRoute allow={['admin', 'manager']}><Live /></RoleRoute>} />
            <Route path="teams" element={<RoleRoute allow={['admin', 'manager']}><Teams /></RoleRoute>} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="screenshots" element={<Screenshots />} />
            <Route path="activity" element={<Activity />} />
            <Route path="settings" element={<RoleRoute allow={['admin', 'manager']}><Settings /></RoleRoute>} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </Router>
    </>
  )
}
